import { useEffect, useState, type FormEvent } from "react";
import { navLinks } from "@/mocks/home";

type SiteHeaderProps = {
  onSearch: (value: string) => void;
  onOpenSubmit: () => void;
};

export default function SiteHeader({ onSearch, onOpenSubmit }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    onSearch(keyword.trim());
    setMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-300 bg-background-50/95 backdrop-blur-md"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="flex h-16 w-full items-center gap-4 px-4 md:h-[72px] md:px-6">
        <a href="#top" className="group flex shrink-0 items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 font-heading text-lg font-bold text-background-50">
            15
          </span>
          <span className="flex flex-col leading-none">
            <h1 className="font-heading text-lg font-bold tracking-tight text-foreground-950 md:text-xl">
              15黑料网
            </h1>
            <span className="mt-0.5 hidden text-[10px] font-label tracking-[0.2em] text-foreground-500 sm:block">
              匿名投稿 · 第一手渠道
            </span>
          </span>
        </a>

        <nav className="ml-2 hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm font-label text-foreground-700 transition-colors hover:bg-background-200 hover:text-primary-700"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <form
            onSubmit={handleSearch}
            className="hidden items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 transition-colors focus-within:border-primary-400 md:flex"
          >
            <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索线报关键词"
              className="w-32 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500 lg:w-44"
            />
          </form>

          <button
            type="button"
            onClick={onOpenSubmit}
            className="hidden cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 sm:flex"
          >
            <i className="ri-add-circle-line text-base" />
            匿名投稿
          </button>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
          <form
            onSubmit={handleSearch}
            className="mb-4 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5"
          >
            <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索线报关键词"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            />
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="cursor-pointer rounded-md bg-background-100 px-3 py-2.5 text-sm font-label text-foreground-800 transition-colors hover:bg-background-200"
              >
                {link.label}
              </a>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onOpenSubmit();
            }}
            className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-add-circle-line text-base" />
            匿名投稿
          </button>
        </div>
      )}
    </header>
  );
}